import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  Box,
  Container,
  Divider,
  Grid2,
  Stack,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { androidBtn, footerLogo, iosBtn } from "../../../assets";
import { AppLink, LocalStorage } from "../../../constant/enum";
import { Routes } from "../../../constant/routeContants";
import { GenericObject } from "../../../interfaces/commonInterface";
import en from "../../../locales/en.json";
import { saveSelectedCity } from "../../../redux/actions/commonActions";
import { RootState } from "../../../redux/store";
import {
  TypographyConstants,
  statusCode,
} from "../../../rental-src/constants/enum";
import { locationPageData } from "../../../rental-src/constants/LocationConstants";
import { colors } from "../../../themes/colors";
import { setLocalStorageData } from "../../../utills/helpers/helper";
import styles from "./index.module.css";
import { memo } from "react";
import { useId } from "react";
import { FooterFreedoCitiesSkeleton } from "../SkeletonLoader/CmsDataSkelton";
interface FooterListInterface {
  title: string;
  list: Array<any>;
  isLink: Boolean;
  ind: number;
  pathname: string;
}

const FooterNew = memo(() => {
  const { footer } = en;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname = "" } = useLocation();
  const randomId = useId();

  const {
    rentalMasterCityList: { cities = [] },
    rentalCityLoader,
  } = useSelector((state: RootState) => state.rentalsCommonReducer);

  //handle navigation on logo click
  const handleFreedoLogo = () => {
    if (pathname === Routes.HOME) window.scrollTo(0, 0);
    navigate(Routes.HOME);
  };

  //handle select city
  const handleSelect = (city: Object | any) => {
    const cityInLocationList: GenericObject | undefined =
      locationPageData?.find(
        (loc: GenericObject) => loc?.cityId === city?.name
      );

    setLocalStorageData(LocalStorage.userCity, city);
    dispatch(saveSelectedCity(city));

    if (cityInLocationList) {
      navigate(cityInLocationList?.pathname); // navigate to location page
    } else {
      window.scrollTo(0, 0);
    }
  };

  return (
    <Box
      key={randomId}
      className={styles.footer}
      minHeight={{
        xl: "530px",
        lg: "530px",
        md: "530px",
        sm: "790px",
        xs: "1190px",
      }}
    >
      <Container>
        <img
          src={footerLogo}
          alt="footerLogo"
          onClick={handleFreedoLogo}
          style={{
            cursor: "pointer",
            marginBottom: "20px",
            width: "200px",
            height: "70px",
          }}
          loading="lazy"
        />

        <Grid2
          container
          spacing={3}
          sx={{ display: "flex", flexDirection: "row", minHeight: "105px" }}
        >
          {footer?.listItems?.map(({ title, list, isLink, id }, ind) => (
            <FooterList
              key={ind}
              ind={ind}
              title={title}
              list={list}
              isLink={isLink}
              pathname={pathname}
            />
          ))}
        </Grid2>

        {/* download oup app section */}
        <Grid2
          container
          spacing={3}
          sx={{ display: "flex", flexDirection: "row", minHeight: "80px" }}
        >
          <Grid2
            size={{ xs: 12, sm: 6, md: 6, lg: 9 }}
            className={styles.footerListItemDownload}
          >
            <Typography
              variant="h6"
              color={colors.white}
              fontWeight={500}
              textTransform="uppercase"
            >
              {footer.DownloadOurApp}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: "10px",
              }}
            >
              <Link target="_blank" to={AppLink.appStoreLink}>
                <img
                  src={iosBtn}
                  alt="Download from app store"
                  style={{ width: "160px", height: "50px" }}
                  loading="lazy"
                />
              </Link>

              <Link target="_blank" to={AppLink.playstoreLink}>
                <img
                  src={androidBtn}
                  alt="Download from play store"
                  style={{ width: "160px", height: "50px" }}
                  loading="lazy"
                />
              </Link>
            </Box>
          </Grid2>
          <Grid2
            size={{ xs: 12, sm: 6, md: 6, lg: 3 }}
            marginTop={"10px"}
            sx={{ textAlign: { sm: "left", xs: "center" } }}
          >
            <Link to={Routes.CONTACT}>{footer.GetInTouch}</Link>
          </Grid2>
        </Grid2>
        <Divider className={styles.divider} />

        {/* City list section */}
        <Stack gap="20px" minHeight={"180px"}>
          <Typography
            variant={TypographyConstants.H5}
            sx={{
              color: colors.white,
              fontWeight: 500,
              textTransform: "capitalize",
              textDecoration: "underline",
            }}
          >
            {footer.FreedoCities}
          </Typography>
          <Box
            sx={{
              overflowY: "scroll",
              scrollBehavior: "smooth",
              scrollbarWidth: "none",
            }}
          >
            {rentalCityLoader ? (
              <FooterFreedoCitiesSkeleton />
            ) : (
              <Grid2
                container
                spacing={2}
                padding={{ sm: "20px" }}
                width={{ xs: "112%", sm: "unset" }}
              >
                {cities &&
                  cities.map((city: any, ind: number) =>
                    city.status === statusCode.ACTIVE ? (
                      <Grid2
                        size={{ xs: 6, sm: 4, md: 3, lg: 2 }}
                        className={styles.footerCityList}
                        onClick={() => handleSelect(city)}
                        key={`cityItem-${ind}`}
                      >
                        <Link to="">
                          <LocationOnIcon
                            sx={{ width: "20px", height: "16px" }}
                          />
                          <Typography
                            variant={TypographyConstants.CAPTION}
                            color={colors.white}
                            sx={{
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {footer.BikeRentIn}{" "}
                            <strong>{city.displayName} </strong>
                          </Typography>
                        </Link>
                      </Grid2>
                    ) : null
                  )}
              </Grid2>
            )}
          </Box>
        </Stack>
      </Container>
    </Box>
  );
});

const FooterList = ({
  title,
  list,
  isLink,
  ind,
  pathname,
}: FooterListInterface) => (
  <Grid2
    key={`grid-${ind}`}
    size={{ xs: 12, sm: 6, md: 6, lg: 3 }}
    className={styles.footerListItems}
  >
    <Typography
      variant={TypographyConstants.H6}
      color={colors.white}
      fontWeight={500}
      textTransform="uppercase"
    >
      {title}
    </Typography>
    <Stack className={styles.footerListItem}>
      {list?.map((item, ind) => {
        return (
          <Box
            key={`listItem-${ind}`}
            className={!isLink ? styles.textFooter : ""}
          >
            {isLink ? (
              <Link to={item.link} key={`listLink-${ind}`}>
                <Typography
                  variant={TypographyConstants.CAPTION}
                  color={colors.white}
                  sx={{
                    "&:hover": {
                      color: colors.THEME_BLUE,
                      textDecoration: "underline",
                    },
                  }}
                >
                  {item.label}
                </Typography>
              </Link>
            ) : (
              <Typography
                variant={TypographyConstants.CAPTION}
                color={colors.white}
              >
                {item.label}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  </Grid2>
);

export default FooterNew;
